import {
  RootDispatch,
  RootGetState,
  NavigationStep,
  CommunicationHistory,
  FamilyMember,
  Page
} from "../types";
import history from "../../history";
import { initiateVideoChat } from "../videoChat/actions";
import flash from "../flash/actions";
import { getLocations, getPatientList } from "../dashboard/actions";
import { fetchOrgs } from "../organizations/actions";
import { del, get, post, put } from "../../utils/fetch";
import { cleanPatientResponse, Patient, PatientFormParams, PatientResponse } from "./types";
import { FormikErrors } from "formik";
import { VideoChatInviteMode } from "../videoChat/types";

export type Action =
  | { type: "patient/setLoading"; isLoading: boolean }
  | { type: "patient/getPatient-started" }
  | { type: "patient/getPatient-success"; patient: Patient }
  | { type: "patient/getPatient-failure"; errorMessage: string }
  | { type: "patient/deactivatePatient-started" }
  | { type: "patient/deactivatePatient-success" }
  | { type: "patient/deactivatePatient-failure"; errorMessage: string }
  | { type: "patient/getHistory-started" }
  | { type: "patient/getHistory-success"; histories: Page<CommunicationHistory> }
  | { type: "patient/getHistory-failure"; errorMessage: string }
  | { type: "patient/getFamily-started" }
  | { type: "patient/getFamily-success"; familyMembers: Page<FamilyMember> }
  | { type: "patient/getFamily-failure"; errorMessage: string }
  | { type: "patient/addFamilyMember-started" }
  | { type: "patient/addFamilyMember-success"; familyMember: FamilyMember }
  | { type: "patient/addFamilyMember-failure"; errorMessage: string }
  | { type: "patient/deleteFamilyMember-started" }
  | { type: "patient/deleteFamilyMember-success" }
  | { type: "patient/deleteFamilyMember-failure"; errorMessage: string }
  | { type: "patient/getMoreHistory-started" }
  | { type: "patient/getMoreHistory-success"; histories: Page<CommunicationHistory> }
  | { type: "patient/getMoreHistory-failure"; errorMessage: string }
  | { type: "patient/setPatient"; patient: Patient }
  | { type: "patient/addPatient-started" }
  | { type: "patient/addPatient-success" }
  | { type: "patient/addPatient-failure"; errorMessage: string }
  | { type: "patient/editPatient-started" }
  | { type: "patient/editPatient-success"; patient: Patient }
  | { type: "patient/editPatient-failure"; errorMessage: string };

export const getPatient = (id: string) => async (
  dispatch: RootDispatch,
  getState: RootGetState
) => {
  dispatch({
    type: "patient/getPatient-started"
  });

  const url =
    "/api/organization/" + getState().orgs.currentOrganization?.id + "/patients/" + id + "/";

  const resp = await get<PatientResponse>(url);

  if (resp.error) {
    dispatch({
      type: "patient/getPatient-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "patient/getPatient-success",
      patient: cleanPatientResponse(resp.data)
    });
    dispatch(getHistories());
    dispatch(getFamilyMembers(resp.data.id));
  } else {
    // TODO Logging
  }
};

export const deactivatePatient = (patient: Patient) => async (
  dispatch: RootDispatch,
  getState: RootGetState
) => {
  dispatch({
    type: "patient/deactivatePatient-started"
  });

  const url =
    "/api/organization/" +
    getState().orgs.currentOrganization?.id +
    "/patients/" +
    patient.id +
    "/deactivate/";

  const resp = await post(url, patient);

  if (resp.error) {
    dispatch({
      type: "patient/deactivatePatient-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "patient/deactivatePatient-success"
    });
    dispatch({
      type: "dashboard/removePatientFromList",
      patient: patient
    });

    dispatch(getPatient(patient?.id));
    dispatch(getLocations());

    flash(patient?.givenName + " " + patient?.familyName + " has been deactivated.", "info");
  }
};

export const setPatientAndAct = (patient: Patient, step: NavigationStep) => async (
  dispatch: RootDispatch
) => {
  dispatch({
    type: "patient/setPatient",
    patient: patient
  });

  switch (step) {
    case NavigationStep.PatientDetail:
      history.push(`/patient/${patient.id}/`);
      break;
    case NavigationStep.MessagePatient:
      history.push(`/patient/${patient.id}/message/`);
      break;
    case NavigationStep.MessageFamily:
      history.push(`/patient/${patient.id}/message/family/`);
      break;
    case NavigationStep.VideoChatSms:
      dispatch(initiateVideoChat(VideoChatInviteMode.Sms));
      break;
    case NavigationStep.VideoChatEmail:
      dispatch(initiateVideoChat(VideoChatInviteMode.Email));
      break;
  }
};

export const getHistories = () => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "patient/getHistory-started"
  });

  const url =
    "/api/organization/" +
    getState().orgs.currentOrganization?.id +
    "/patients/" +
    getState().patientdetail.data.patient?.id +
    "/histories/";

  const resp = await get<Page<CommunicationHistory>>(url);

  if (resp.error) {
    dispatch({
      type: "patient/getHistory-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "patient/getHistory-success",
      histories: resp.data
    });
  } else {
    // TODO logging
  }
};

export const getFamilyMembers = (patientId: string) => async (
  dispatch: RootDispatch,
  getState: RootGetState
) => {
  dispatch({
    type: "patient/getFamily-started"
  });

  const url =
    "/api/organization/" +
    getState().orgs.currentOrganization?.id +
    "/patients/" +
    patientId +
    "/familyMembers/";

  const resp = await get<Page<FamilyMember>>(url);

  if (resp.error) {
    dispatch({
      type: "patient/getFamily-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "patient/getFamily-success",
      familyMembers: resp.data
    });
  } else {
    // TODO Logging
  }
};

export const addFamilyMember = (familyMember: FamilyMember) => async (
  dispatch: RootDispatch,
  getState: RootGetState
) => {
  dispatch({
    type: "patient/addFamilyMember-started"
  });

  const url =
    "/api/organization/" +
    getState().orgs.currentOrganization?.id +
    "/patients/" +
    getState().patientdetail.data.patient?.id +
    "/familyMembers/";

  const resp = await post(url, familyMember);

  if (resp.error) {
    dispatch(flash("Add family member failed: " + resp.error.message, "error"));
    dispatch({
      type: "patient/addFamilyMember-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "patient/addFamilyMember-success"
    });

    const patient = getState().patientdetail.data.patient;
    if (patient) {
      dispatch(getPatient(patient.id ? patient.id : ""));
      dispatch(getFamilyMembers(patient.id));
    }
  }
};

export const deleteFamilyMember = (familyMember: FamilyMember) => async (
  dispatch: RootDispatch,
  getState: RootGetState
) => {
  dispatch({
    type: "patient/deleteFamilyMember-started"
  });

  const url =
    "/api/organization/" +
    getState().orgs.currentOrganization?.id +
    "/patients/" +
    getState().patientdetail.data.patient?.id +
    "/familyMembers/" +
    familyMember.id +
    "/";

  const resp = await del(url);

  if (resp.error) {
    dispatch({
      type: "patient/deleteFamilyMember-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.status === 204) {
    dispatch({
      type: "patient/deleteFamilyMember-success"
    });
    const patient = getState().patientdetail.data.patient;
    if (patient) dispatch(getFamilyMembers(patient.id));
  } else {
    // TODO Logging
  }
};

export const getMoreHistory = () => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "patient/getMoreHistory-started"
  });

  const resp = await get<Page<CommunicationHistory>>(getState().patientdetail.data.histories.next);

  if (resp.error) {
    dispatch({
      type: "patient/getMoreHistory-failure",
      errorMessage: resp.error.message
    });
  } else if (resp.data) {
    dispatch({
      type: "patient/getMoreHistory-success",
      histories: resp.data
    });
  }
};

export const addPatient = (
  params: PatientFormParams,
  onError: (error: FormikErrors<PatientFormParams>) => void
) => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "patient/addPatient-started"
  });

  if (!getState().orgs.currentOrganization) {
    dispatch(flash("No organization is currently selected.", "error"));
    dispatch(fetchOrgs());
  }

  const url = "/api/organization/" + getState().orgs.currentOrganization?.id + "/patients/";

  const resp = await post<PatientResponse>(url, params);

  if (resp.error) {
    dispatch(flash("Add patient failed: " + resp.error.message, "error"));
    dispatch({ type: "patient/addPatient-failure", errorMessage: resp.error.message });
    onError(resp.error.fieldErrors as FormikErrors<PatientFormParams>);
  } else if (resp.data) {
    const newPatient = cleanPatientResponse(resp.data);
    dispatch({ type: "patient/addPatient-success" });
    dispatch({ type: "patient/setPatient", patient: newPatient });
    dispatch(getPatientList());
    dispatch(getLocations());
    history.push(`/patient/${newPatient.id}`);
  } else {
    // TODO Logging
  }
};

export const editPatient = (
  params: PatientFormParams,
  onError: (error: FormikErrors<PatientFormParams>) => void,
  id?: string
) => async (dispatch: RootDispatch, getState: RootGetState) => {
  dispatch({
    type: "patient/editPatient-started"
  });

  const url =
    "/api/organization/" + getState().orgs.currentOrganization?.id + "/patients/" + id + "/";

  const resp = await put<PatientResponse>(url, params);

  if (resp.error) {
    dispatch({
      type: "patient/editPatient-failure",
      errorMessage: resp.error.message
    });
    onError(resp.error.fieldErrors as FormikErrors<PatientFormParams>);
    dispatch(flash("Add patient failed: " + resp.error.message, "error"));
  } else if (resp.data) {
    dispatch({
      type: "patient/editPatient-success",
      patient: cleanPatientResponse(resp.data)
    });
    dispatch({
      type: "patient/setPatient",
      patient: cleanPatientResponse(resp.data)
    });
    dispatch(getLocations());
    history.push(`/patient/${resp.data.id}`);
  }
};
